import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import Helper from "@/helper";

const generateIndexes = (max, shouldSkip) =>
  Array.from({ length: max }, (_, i) => i).filter((i) => !shouldSkip(i));

const useScheduleTime = ({ customerTimezone, timezones }) => {
  const initial = useMemo(() => dayjs().add(3, "minute"), []);
  const [date, setDate] = useState(initial);
  const [hour, setHour] = useState(initial.hour());
  const [minute, setMinute] = useState(initial.minute());
  const [timezone, setTimezone] = useState(customerTimezone || timezones?.[0]?.[1] || "UTC");

  const offsetMinutes = useMemo(() => {
    return Helper.getUTCOffset(timezone, date.toDate());
  }, [timezone, date]);

  const nowInTimezone = useMemo(() => {
    return dayjs().add(offsetMinutes - dayjs().utcOffset(), "minute");
  }, [offsetMinutes]);

  const scheduledDate = useMemo(() => {
    return date.hour(hour).minute(minute).second(0);
  }, [date, hour, minute]);

  const scheduledAt = useMemo(() => {
    return scheduledDate.subtract(offsetMinutes, "minute").format("YYYY-MM-DD HH:mm:ss");
  }, [scheduledDate, offsetMinutes]);

  const disabledDate = useMemo(
    () => (current) => {
      if (!current) return true;
      const today = nowInTimezone.startOf("day");
      const maxDate = today.add(1, "month");
      return current.isBefore(today) || current.isAfter(maxDate);
    },
    [nowInTimezone],
  );

  const hourOptions = useMemo(() => {
    return generateIndexes(
      24,
      (i) => date.isSame(nowInTimezone, "day") && i < nowInTimezone.hour(),
    );
  }, [date, nowInTimezone]);

  const minuteOptions = useMemo(() => {
    return generateIndexes(
      60,
      (i) =>
        date.isSame(nowInTimezone, "day") &&
        hour === nowInTimezone.hour() &&
        i < nowInTimezone.minute() + 3,
    );
  }, [date, hour, nowInTimezone]);

  useEffect(() => {
    if (date.isSame(nowInTimezone, "day") && hour === nowInTimezone.hour()) {
      const minMinute = nowInTimezone.minute() + 3;
      if (minute < minMinute) {
        setMinute(minMinute);
      }
    }
  }, [date, hour, minute, nowInTimezone]);

  useEffect(() => {
    if (scheduledDate.isBefore(nowInTimezone)) {
      const adjusted = nowInTimezone.add(3, "minute");
      setDate(adjusted);
      setHour(adjusted.hour());
      setMinute(adjusted.minute());
    }
  }, [timezone, scheduledDate, nowInTimezone]);

  return {
    date,
    setDate,
    hour,
    setHour,
    minute,
    setMinute,
    timezone,
    setTimezone,
    disabledDate,
    hourOptions,
    minuteOptions,
    scheduledAt,
  };
};

export default useScheduleTime;
